import React, { FC, ReactNode, createContext, useContext } from 'react';
import { ThemeProvider, useTheme } from '@airwallex/theme';
import type { ThemeProviderProps } from '@airwallex/theme';

/**
 * Brand-only theme override for the RHS payment section (ADV-6447).
 *
 * The payment element (the RHS) must be themed by the merchant `colorBrand`
 * ONLY — `colorText` and `colorBackground` must not affect it. The surrounding
 * page (LHS) is themed with the full override (brand + text) by
 * `ThemedBrandingShell`; this context carries a separate override built from
 * `colorBrand` alone so the payment subtree can reset its text and surfaces back
 * to the FEC defaults while keeping only the brand accents.
 *
 * `undefined` means no brand color (un-themed, or the merchant set no brand), in
 * which case `PaymentThemeScope` is a transparent pass-through and production
 * renders bit-for-bit unchanged.
 */
type PaymentThemeOverride = ThemeProviderProps['themeOverride'];

type PaymentThemeValue = {
  // Whether the surrounding page is merchant-themed. When true, the RHS payment
  // subtree is re-themed by rebuilding from the FEC base tokens so it never
  // inherits the LHS order-info theme — even when there is no brand override to
  // apply (e.g. a merchant who set a background but no brand color).
  active: boolean;
  // The brand-only override applied on top of the base, or `undefined` to reset
  // the subtree to the plain FEC base.
  override?: PaymentThemeOverride;
};

const PaymentThemeContext = createContext<PaymentThemeValue>({ active: false });

export const PaymentThemeProvider: FC<{ value: PaymentThemeValue; children: ReactNode }> = ({ value, children }) => (
  <PaymentThemeContext.Provider value={value}>{children}</PaymentThemeContext.Provider>
);

// The payment subtree's theme intent: whether to re-theme, and the brand-only
// override to apply when doing so.
export const usePaymentTheme = (): PaymentThemeValue => useContext(PaymentThemeContext);

// Re-themes the payment subtree (RHS) when the page is merchant-themed. A nested
// ThemeProvider rebuilds the theme from the FEC base tokens — it does NOT inherit
// the parent (LHS) colorText/colorBackground theme — so the payment section's
// text and surfaces fall back to the neutral defaults and only the merchant brand
// accents remain (ADV-6447). langKey is inherited from the surrounding theme so
// non-English checkouts keep their localized typography. Pass-through (no extra
// DOM node, no theme change) when the page is un-themed.
export const PaymentThemeScope: FC<{ children: ReactNode }> = ({ children }) => {
  const { active, override } = usePaymentTheme();
  const { langKey } = useTheme();
  if (!active) return <React.Fragment>{children}</React.Fragment>;
  return (
    <ThemeProvider langKey={langKey} themeOverride={override}>
      {children}
    </ThemeProvider>
  );
};
